"use client";

import React, { useCallback, useEffect } from "react";
import Link from "next/link";
import MenuItem from "../../MenuItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleUser,
  faMagnifyingGlass,
} from "@fortawesome/sharp-light-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import { HeaderProps } from "../models";
import SearchBar from "@/components/ui/SearchBar";
import { getPaddings, getTextSize } from "@/utils/theme-helper";

const DefaultHeader: React.FC<HeaderProps> = ({ cmsData, appData }) => {
  const logoUrl = appData?.logo?.originalUrl;
  const [showMenu, setShowMenu] = React.useState(false);
  const [showSearch, setShowSearch] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [lastScrollY, setLastScrollY] = React.useState(0);

  const handleScroll = useCallback(() => {
    if (!window) {
      return;
    }

    if (window.scrollY > lastScrollY && window.scrollY > 1) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    setLastScrollY(window.scrollY);
  }, [lastScrollY]);

  useEffect(() => {
    if (!window) {
      return;
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const menuItems = cmsData?.pages
    .filter((x) => x.includeInMenu)
    .map((page) => ({
      title: page.menuTitle,
      href: page.slug,
      redirectToExternal: page.redirectToExternal,
      openInNewTab: page.openInNewTab,
      externalUrl: page.externalUrl,
    }));

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const toggleSearch = () => {
    setShowSearch(!showSearch);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showSearch) {
        setShowSearch(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSearch]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showMenu) {
        setShowMenu(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showMenu]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setShowSearch(false);
        setShowMenu(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  let customerPortalUrl =
    cmsData?.b2cLink ?? appData?.resourceGroup?.primaryB2CApplication?.url;
  if (customerPortalUrl && !customerPortalUrl.startsWith("https://")) {
    customerPortalUrl = "https://" + customerPortalUrl.replace("http://", "");
  }

  return (
    <div id="DefaultHeader">
      <div
        id="layover"
        className={`fixed inset-0 bg-gray-800 z-30 transition-opacity duration-300 ${
          showMenu ? "opacity-40" : "opacity-0 pointer-events-none"
        }`}
      ></div>
      <header
        className={`h-[80px] text-[var(--header-text-color)] lg:h-[100px] flex justify-between bg-[var(--header-bg-color)] fixed top-0 left-0 right-0 z-30 transition-transform duration-300 ${
          isVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <AnimatePresence>
          {showMenu && (
            <motion.div
              initial="hidden"
              animate="visible"
              className={`flex text-[var(--menu-text-color)] h-dvh fixed top-0 z-20 right-0 left-0 lg:left-auto bottom-0 lg:w-[380px] pt-28 pl-[30px]`}
            >
              <motion.div
                className="bg-[var(--menu-bg-color)] absolute inset-0 z-20"
                variants={{
                  visible: {
                    clipPath: `circle(300% at calc(100% - 54px) 50px)`,
                    transition: {
                      type: "spring",
                      stiffness: 20,
                      restDelta: 2,
                    },
                  },
                  hidden: {
                    clipPath: "circle(0px at calc(100% - 54px) 50px)",
                    transition: {
                      delay: 0.2,
                      type: "spring",
                      stiffness: 400,
                      damping: 40,
                    },
                  },
                }}
              ></motion.div>
              <motion.div
                variants={{
                  visible: {
                    transition: { staggerChildren: 0.02, delayChildren: 0.3 },
                  },
                  hidden: {
                    transition: { staggerChildren: 0.02, staggerDirection: -1 },
                  },
                }}
                className={`flex flex-col gap-4 relative  z-20 flex-1 overflow-y-auto pb-10 ${getTextSize(cmsData?.layout?.header?.menuTextSize, "lg")}`}
              >
                {menuItems?.map((item, index) => (
                  <motion.div
                    key={`${index}-menu`}
                    variants={{
                      visible: {
                        y: 0,
                        opacity: 1,
                      },
                      hidden: {
                        y: 50,
                        opacity: 0,
                        transition: {
                          duration: 0.05,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      key={item.href}
                      href={item.href}
                      onClick={() => setShowMenu(false)}
                      redirectToExternal={item.redirectToExternal}
                      openInNewTab={item.openInNewTab}
                      externalUrl={item.externalUrl}
                    >
                      {item.title}
                    </MenuItem>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          className={`flex px-[20px] lg:px-[40px] flex-1 justify-between bg-[var(--header-bg-color)]`}
        >
          <Link aria-label="Home" className="flex" href={"/"}>
            {(cmsData?.logoUrl || logoUrl) && (
              <img
                src={logoUrl ?? cmsData?.logoUrl}
                alt="logo"
                height={0}
                width={150}
                className={`py-4 h-[80px] lg:h-[100px] w-auto  ${cmsData?.layout?.header?.paddingDefault ? getPaddings(cmsData?.layout?.header?.paddingDefault) : ""}`}
              />
            )}
          </Link>
          <div className="flex gap-4 lg:gap-[34px] items-center justify-end">
            <button
              aria-label="Search"
              className="p-[8px] flex "
              onClick={() => toggleSearch()}
            >
              <FontAwesomeIcon
                className="size-[24px] flex"
                icon={faMagnifyingGlass}
              />
            </button>
            {customerPortalUrl && (
              <Link
                aria-label="Kundeunivers"
                target="_blank"
                href={customerPortalUrl}
                className="p-[8px] flex"
              >
                <FontAwesomeIcon
                  className="size-[24px] flex"
                  icon={faCircleUser}
                />
              </Link>
            )}
            <button
              aria-label="Menu"
              onClick={() => (showSearch ? toggleSearch() : toggleMenu())}
              className="z-[60] size-10 relative focus:outline-none rounded group"
            >
              <div className="block w-7 absolute left-6 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span
                  className={`block absolute right-0 h-0.5  group-hover:w-full bg-current transition-all transform duration-300 ease-in-out
                    ${showMenu ? "text-[var(--menu-text-color)]" : "text-[var(--header-text-color)]"}
                     ${
                       showMenu || showSearch
                         ? "rotate-45 w-full "
                         : "-translate-y-2 w-[70%]"
                     }`}
                ></span>
                <span
                  className={`block absolute right-0 h-0.5  group-hover:w-full bg-current  transition-all  transform duration-300 ease-in-out ${showMenu ? "text-[var(--menu-text-color)]" : "text-[var(--header-text-color)]"}
                                        ${
                                          showMenu || showSearch
                                            ? "opacity-0"
                                            : "w-[60%]"
                                        }`}
                ></span>
                <span
                  className={`block absolute right-0 h-0.5 w-full bg-current transform  transition duration-300 ease-in-out ${showMenu ? "text-[var(--menu-text-color)]" : "text-[var(--header-text-color)]"}
                   ${showMenu || showSearch ? "-rotate-45" : "translate-y-2"}`}
                ></span>
              </div>
            </button>
          </div>
        </div>
      </header>
      <SearchBar
        cmsData={cmsData!}
        toggleSearch={() => toggleSearch()}
        showSearch={showSearch}
      />
    </div>
  );
};

export default DefaultHeader;
